<script setup lang="ts">
/**
 * Prompt Dialog
 * Mount once in App.vue — driven by usePrompt() composable.
 */
import { onMounted, onUnmounted, nextTick, ref, watch } from 'vue'
import { GitCommitHorizontal, Loader2 } from 'lucide-vue-next'
import {
  promptState,
  promptInputValue,
  handlePromptConfirm,
  handlePromptCancel,
  updateInputValue,
} from '@/composables/usePrompt'

const inputRef = ref<HTMLInputElement | HTMLTextAreaElement | null>(null)

function onKeydown(e: KeyboardEvent) {
  if (!promptState.visible) return
  if (e.key === 'Escape') {
    handlePromptCancel()
  }
  if (e.key === 'Enter' && !e.shiftKey && promptState.options.inputType !== 'textarea') {
    e.preventDefault()
    handlePromptConfirm()
  }
}

function onOverlayClick(e: MouseEvent) {
  if ((e.target as HTMLElement) === e.currentTarget) {
    handlePromptCancel()
  }
}

watch(() => promptState.visible, async (visible) => {
  if (visible) {
    await nextTick()
    inputRef.value?.focus()
    inputRef.value?.select()
  }
})

onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="animate-fade-in"
      leave-active-class="animate-fade-out"
    >
      <div
        v-if="promptState.visible"
        class="fixed inset-0 bg-black/60 backdrop-blur-[4px] z-[10002] flex items-center justify-center p-5"
        @click="onOverlayClick"
      >
        <div
          class="bg-surface rounded-lg shadow-lg border border-border max-w-[460px] w-full p-6 animate-modal-slide-in"
          role="dialog"
          aria-modal="true"
        >
          <!-- Icon + Title -->
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 flex items-center justify-center rounded-full bg-green-500/10 text-green-600 shrink-0">
              <GitCommitHorizontal :size="20" />
            </div>
            <h3 class="text-lg font-semibold text-foreground">
              {{ promptState.options.title }}
            </h3>
          </div>

          <!-- Message -->
          <p v-if="promptState.options.message" class="text-sm text-foreground-secondary mb-3 leading-relaxed">
            {{ promptState.options.message }}
          </p>

          <!-- Input -->
          <div class="relative">
            <textarea
              v-if="promptState.options.inputType === 'textarea'"
              ref="inputRef"
              :value="promptInputValue"
              :placeholder="promptState.options.placeholder"
              :disabled="promptState.loading"
              rows="3"
              class="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface text-foreground placeholder:text-foreground-tertiary focus:outline-none focus:ring-2 focus:ring-accent-primary/50 resize-none disabled:opacity-70 disabled:cursor-wait"
              @input="updateInputValue(($event.target as HTMLTextAreaElement).value)"
            />
            <input
              v-else
              ref="inputRef"
              type="text"
              :value="promptInputValue"
              :placeholder="promptState.options.placeholder"
              :disabled="promptState.loading"
              class="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface text-foreground placeholder:text-foreground-tertiary focus:outline-none focus:ring-2 focus:ring-accent-primary/50 disabled:opacity-70 disabled:cursor-wait"
              @input="updateInputValue(($event.target as HTMLInputElement).value)"
            />
            <div
              v-if="promptState.loading"
              class="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-foreground-muted"
            >
              <Loader2 :size="16" class="animate-spin" />
            </div>
          </div>

          <!-- Buttons -->
          <div class="flex gap-3 justify-end mt-5">
            <button class="btn btn-secondary min-w-[90px]" @click="handlePromptCancel">
              {{ promptState.options.cancelText }}
            </button>
            <button
              class="btn btn-success min-w-[90px] flex items-center justify-center gap-1.5"
              :disabled="promptState.loading"
              @click="handlePromptConfirm"
            >
              <Loader2 v-if="promptState.loading" :size="14" class="animate-spin shrink-0" />
              {{ promptState.options.confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
